import React, { useState, useEffect, useRef, memo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import UserDashboardLayout from "../../components/layout/UserDashboardLayout"
import Alert from "../../components/common/alert"
import { getAllCourses, getAllCourseModules, getLessonsByModuleId } from "../../api/courseService"
import { getPurchaseData, getPackageById, getCourseProgress, type PurchasedCourseWithDetails, type Module, type Lesson } from "../../api/packageService"
import Hls from 'hls.js'

import { CheckCircle, Clock, PlayCircle, Search, FileText, BookOpen, X, Package, Calendar, Eye, List } from "lucide-react"
import ReactMarkdown from "react-markdown"

// Add interface for course with purchaseId
interface PurchasedCourseWithPurchaseId extends PurchasedCourseWithDetails {
  // Removed purchaseId since we can't get it from the API
  expiry?: string;
  daysLeft?: number;
}

// Add interface for package with purchaseId
interface PurchasedPackageWithPurchaseId {
  packageId: string | null;
  packageName: string;
  packageThumbnail: string;
  createdAt: string;
  courses: PurchasedCourseWithPurchaseId[];
}

const UserPurchase: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [purchasedPackages, setPurchasedPackages] = useState<PurchasedPackageWithPurchaseId[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<{
    type: "success" | "error" | "info" | "warning";
    message: string;
  } | null>(null);
  const [expandedPackages, setExpandedPackages] = useState<Record<string, boolean>>({});
  const [, setCourseProgress] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState<string>("all"); // Add filter state
  
  // Helper function to determine if a URL is a video
  const isVideoUrl = (url: string): boolean => {
    // Check for data URLs - only video data URLs
    if (url.startsWith('data:video')) {
      return true;
    }
    
    // Check for streaming URLs
    if (url.includes('/stream/')) {
      return true;
    }
    
    // Check for file extensions in the URL
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    return videoExtensions.some(ext => url.includes(ext));
  };
  
  // Helper function to get course thumbnail with proper token handling
  const getCourseThumbnail = (thumbnailUrl: string): string => {
    if (!thumbnailUrl) return "/placeholder.svg";
    
    // Check if it's already a data URL
    if (thumbnailUrl.startsWith('data:')) {
      return thumbnailUrl;
    }
    
    // Check if token is already present in the URL
    if (thumbnailUrl.includes('token=')) {
      return thumbnailUrl;
    }
    
    // Check if it's a streaming URL
    if (thumbnailUrl.includes('/stream/')) {
      // For streaming URLs, we might need to append the token differently
      const authToken = localStorage.getItem("AUTH_TOKEN");
      if (authToken) {
        // For streaming URLs, the token is usually part of the URL path, not a query parameter
        // But we'll still append it as a query parameter for consistency
        const separator = thumbnailUrl.includes('?') ? '&' : '?';
        const urlWithToken = `${thumbnailUrl}${separator}token=${encodeURIComponent(authToken)}`;
        return urlWithToken;
      }
      return thumbnailUrl;
    }
    
    const authToken = localStorage.getItem("AUTH_TOKEN");
    if (authToken) {
      const separator = thumbnailUrl.includes('?') ? '&' : '?';
      const urlWithToken = `${thumbnailUrl}${separator}token=${encodeURIComponent(authToken)}`;
      return urlWithToken;
    }
    return thumbnailUrl;
  };
  
  // Dedicated Video Component with optimized rendering
  const OptimizedVideoThumbnail = memo(({ src, alt }: { src: string; alt: string }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    
    useEffect(() => {
      if (!videoRef.current) return;
      
      // Reset states
      setIsLoading(true);
      setHasError(false);
      
      // Clean up previous HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      
      const videoElement = videoRef.current;
      
      // Handle video events
      const handleLoadedData = () => {
        setIsLoading(false);
      };
      
      const handleError = (e: any) => {
        console.error("Video error for:", src, e);
        setIsLoading(false);
        setHasError(true);
        // Fallback to placeholder
        if (videoElement) {
          videoElement.src = "/placeholder.svg";
        }
      };
      
      videoElement.addEventListener('loadeddata', handleLoadedData);
      videoElement.addEventListener('error', handleError);
      
      // Check if this is a streaming URL that needs HLS
      if (src.includes('/stream/') || src.includes('.m3u8')) {
        if (Hls.isSupported()) {
          // Initialize HLS player
          const hls = new Hls({
            enableWorker: true,
          });
          
          hls.loadSource(src);
          hls.attachMedia(videoElement);
          
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            console.log("HLS manifest parsed successfully for:", src);
            setIsLoading(false);
          });
          
          hls.on(Hls.Events.ERROR, (event, data) => {
            console.error("HLS error for:", src, data);
            if (data.fatal) {
              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  hls.startLoad();
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  hls.recoverMediaError();
                  break;
                default:
                  hls.destroy();
                  setHasError(true);
                  setIsLoading(false);
                  break;
              }
            }
          });
          
          hlsRef.current = hls;
        } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
          // For Safari
          videoElement.src = src;
          // Try to play after setting src
          videoElement.play().catch((e: any) => {
            console.log("Autoplay prevented:", e);
          });
        } else {
          // Fallback for browsers that don't support HLS
          videoElement.src = src;
        }
      } else {
        // For regular video files
        videoElement.src = src;
      }
      
      // Cleanup
      return () => {
        videoElement.removeEventListener('loadeddata', handleLoadedData);
        videoElement.removeEventListener('error', handleError);
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      };
    }, [src]);
    
    return (
      <div className="relative w-full h-full">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}
        {hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
            <div className="text-white text-sm">Failed to load video</div>
          </div>
        )}
        <video
          ref={videoRef}
          className="w-full h-full object-cover transition-opacity duration-300"
          style={{ backgroundColor: 'black', opacity: isLoading || hasError ? 0 : 1 }}
          onError={(e) => {
            console.error("Video element error:", e);
            setHasError(true);
            setIsLoading(false);
          }}
        />
      </div>
    );
  });

  // Dedicated Image Component with optimized rendering
  const OptimizedImageThumbnail = memo(({ src, alt }: { src: string; alt: string }) => {
    return (
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.src = "/placeholder.svg";
        }}
      />
    );
  });
  
  // Get filter from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('filter');
    if (filterParam && ['all', 'in_progress', 'completed', 'yet_to_start'].includes(filterParam)) {
      setFilter(filterParam);
    } else {
      setFilter('all'); // Default to 'all' if no valid filter parameter
    }
  }, [location.search]);
  
  // Toggle package expansion
  const togglePackage = (packageId: string) => {
    setExpandedPackages(prev => ({
      ...prev,
      [packageId]: !prev[packageId]
    }));
  };

  // Fetch the purchased data
  const fetchPurchasedData = async () => {
    try {
      setLoading(true);

      // const deliveryPackage = await getPackageById("6a9492e5f31ec2dc990158d9");

      // console.log("========== DELIVERY DHAMAKA ==========");
      // console.log(deliveryPackage.data);
      // console.log("======================================");
      
      // Get user ID from localStorage
      const storedUserData = localStorage.getItem('userData');
      if (!storedUserData) {
        setAlert({
          type: "error",
          message: "User data not found. Please log in again.",
        });
        setLoading(false);
        return;
      }
      
      let userId;
      try {
        const userData = JSON.parse(storedUserData);
        userId = userData._id;
      } catch (error) {
        setAlert({
          type: "error",
          message: "Error parsing user data. Please log in again.",
        });
        setLoading(false);
        return;
      }
      
      if (!userId) {
        setAlert({
          type: "error",
          message: "User ID not found. Please log in again.",
        });
        setLoading(false);
        return;
      }
      
      // Fetch purchase data from API
      const purchaseResponse = await getPurchaseData(userId);
      
      if (purchaseResponse.status !== 200 || !purchaseResponse.data) {
        setAlert({
          type: "error",
          message: purchaseResponse.message || "Failed to load purchased data",
        });
        setLoading(false);
        return;
      }
      
      // Group purchases by package
      const packageMap = new Map<string | null, any[]>();
      
      purchaseResponse.data.purchases.forEach((purchase: any) => {
        const key = purchase.packageId || 'individual_courses';
        if (!packageMap.has(key)) {
          packageMap.set(key, []);
        }
        packageMap.get(key)?.push(purchase);
      });
      
      // Process each package
      const processedPackages: PurchasedPackageWithPurchaseId[] = [];
      
      // Fetch all courses first to have the details
      const coursesResponse = await getAllCourses();

      console.log("========== ALL COURSES ==========");
      console.log(coursesResponse.data);
      console.log("=================================");

      if (coursesResponse.status !== 200 || !coursesResponse.data) {
        setAlert({
          type: "error",
          message: coursesResponse.message || "Failed to load course details",
        });
        setLoading(false);
        return;
      }
      
      // Convert Map to Array for iteration
      const packageEntries = Array.from(packageMap.entries());
      
      // Process each package/group
      for (const [packageId, purchases] of packageEntries) {
        if (packageId === 'individual_courses') {
          // Handle individual courses (not in a package)
          const individualCourses: PurchasedCourseWithPurchaseId[] = [];
          
          for (const purchase of purchases) {
            for (const purchasedCourse of purchase.courses) {
              // Find course details
              const courseDetails = coursesResponse.data.find((c: any) => c._id === purchasedCourse.courseId);
              if (!courseDetails) continue;
              
              // Determine status based on view percent
              let status: "yet_to_start" | "in_progress" | "completed" = "yet_to_start";
              
              // Get detailed progress data
              let progressPercent = purchasedCourse.courseViewPercent;
              let courseExpiry: string | undefined;
              let courseDaysLeft: number | undefined;
              
              try {
                const progressResponse = await getCourseProgress(purchasedCourse.courseId, userId);
                if (progressResponse.status === 200 && progressResponse.data) {
                  const detailedProgress = progressResponse.data.getCourseProgress;
                  // Use the total progress percent from detailed data
                  progressPercent = detailedProgress.totalProgressPercent || progressPercent;
                  
                  // Get expiration data
                  courseExpiry = detailedProgress.expiry;
                  courseDaysLeft = detailedProgress.daysLeft ?? undefined;
                  
                  // Calculate average watch time as fallback
                  if (detailedProgress.lessonProgress && detailedProgress.lessonProgress.length > 0) {
                    const totalWatchTime = detailedProgress.lessonProgress.reduce((sum: number, lesson: any) => 
                      sum + (lesson.watchTime || 0), 0);
                    
                    // If we have watch time data, use it to determine if course has been started
                    if (totalWatchTime > 0) {
                      // Override progress with a value that indicates the course has been started
                      if (progressPercent <= 0) {
                        progressPercent = 1; // Set to minimum non-zero value to indicate started
                      }
                    }
                  }
                }
              } catch (error) {
                console.error(`Error fetching detailed progress for course ${purchasedCourse.courseId}:`, error);
              }
              
              // Determine status based on progress
              if (progressPercent <= 0) {
                status = "yet_to_start";
              } else if (progressPercent >= 95) {  // Changed from >= 100 to >= 99
                status = "completed";
              } else {
                status = "in_progress";
              }
              
              // Fetch modules and lessons for this course
              const modulesResponse = await getAllCourseModules();
              let courseModules: Module[] = [];
              
              if (modulesResponse.status === 200 && modulesResponse.data) {
                // Filter modules for this specific course
                const courseModulesData = modulesResponse.data.filter(
                  (module: any) => module.courseId && module.courseId._id === purchasedCourse.courseId
                );
                
                // Fetch lessons for each module
                courseModules = await Promise.all(courseModulesData.map(async (module: any) => {
                  const lessonsResponse = await getLessonsByModuleId(module._id);
                  let lessons: Lesson[] = [];
                  
                  if (lessonsResponse.status === 200 && lessonsResponse.data) {
                    lessons = lessonsResponse.data.map((lesson: any) => ({
                      _id: lesson._id,
                      title: lesson.title,
                      description: lesson.description,
                      lessonType: lesson.lessonType,
                      duration: lesson.duration || 0
                    }));
                  }
                  
                  return {
                    _id: module._id,
                    title: module.title,
                    description: module.description,
                    lessons
                  };
                }));
              }
              
              individualCourses.push({
                _id: purchasedCourse.courseId,
                title: courseDetails.title,
                description: courseDetails.description,
                thumbnail: courseDetails.thumbnail || "",
                createdAt: courseDetails.createdAt,
                language: courseDetails.language,
                desktopAvailable: courseDetails.desktopAvailable,
                PublishStatus: courseDetails.PublishStatus,
                courseViewPercent: purchasedCourse.courseViewPercent,
                certificateSent: purchasedCourse.certificateSent,
                status,
                modules: courseModules,
                moduleCount: courseModules.length,
                lessonCount: courseModules.reduce((total, module) => total + module.lessons.length, 0),
                expiry: courseExpiry,
                daysLeft: courseDaysLeft,
                // Removed purchaseId since we can't get it from the API
              });
              
            }
          }
          
          processedPackages.push({
            packageId: null,
            packageName: "Individual Courses",
            packageThumbnail: "",
            createdAt: new Date().toISOString(),
            courses: individualCourses
          });
        } else {
          // Handle package purchases
          // Fetch package details
          const packageResponse = await getPackageById(packageId as string);
          
          let packageName = `Package ${packageId}`;
          let packageThumbnail = "";
          
          if (packageResponse.status === 200 && packageResponse.data) {
            packageName = packageResponse.data.title;
            packageThumbnail = packageResponse.data.bannerUrl || packageResponse.data.themeUrl || "";
          }
          
          const packageCourses: PurchasedCourseWithPurchaseId[] = [];
          
          // Get all unique course IDs from all purchases in this package
          const courseIds = new Set<string>();
          purchases.forEach((purchase: any) => {
            purchase.courses.forEach((course: any) => {
              courseIds.add(course.courseId);
            });
          });
          
          // Process each course in the package
          for (const courseId of Array.from(courseIds)) {
            // Find the course progress data (from any of the purchases)
            let courseProgressData = { courseViewPercent: 0, certificateSent: false };
            for (const purchase of purchases) {
              const courseData = purchase.courses.find((c: any) => c.courseId === courseId);
              if (courseData) {
                courseProgressData = {
                  courseViewPercent: courseData.courseViewPercent,
                  certificateSent: courseData.certificateSent
                };
                break;
              }
            }
            
            // Find course details
            const courseDetails = coursesResponse.data.find((c: any) => c._id === courseId);
            if (!courseDetails) continue;
            
            // Determine status based on view percent
            let status: "yet_to_start" | "in_progress" | "completed" = "yet_to_start";
            
            // Get detailed progress data
            let progressPercent = courseProgressData.courseViewPercent;
            let courseExpiry: string | undefined;
            let courseDaysLeft: number | undefined;
            
            try {
              const progressResponse = await getCourseProgress(courseId, userId);
              if (progressResponse.status === 200 && progressResponse.data) {
                const detailedProgress = progressResponse.data.getCourseProgress;
                // Use the total progress percent from detailed data
                progressPercent = detailedProgress.totalProgressPercent || progressPercent;
                
                // Get expiration data
                courseExpiry = detailedProgress.expiry;
                courseDaysLeft = detailedProgress.daysLeft ?? undefined;

                // Calculate average watch time as fallback
                if (detailedProgress.lessonProgress && detailedProgress.lessonProgress.length > 0) {
                  const totalWatchTime = detailedProgress.lessonProgress.reduce((sum: number, lesson: any) => 
                    sum + (lesson.watchTime || 0), 0);
                  
                  // If we have watch time data, use it to determine if course has been started
                  if (totalWatchTime > 0) {
                    // Override progress with a value that indicates the course has been started
                    if (progressPercent <= 0) {
                      progressPercent = 1; // Set to minimum non-zero value to indicate started
                    }
                  }
                }
              }
            } catch (error) {
              console.error(`Error fetching detailed progress for course ${courseId}:`, error);
            }
            
            // Determine status based on progress
            if (progressPercent <= 0) {
              status = "yet_to_start";
            } else if (progressPercent >= 99) {  // Changed from >= 100 to >= 99
              status = "completed";
            } else {
              status = "in_progress";
            }
            
            // Fetch modules and lessons for this course
            const modulesResponse = await getAllCourseModules();
            let courseModules: Module[] = [];
            
            if (modulesResponse.status === 200 && modulesResponse.data) {
              // Filter modules for this specific course
              const courseModulesData = modulesResponse.data.filter(
                (module: any) => module.courseId && module.courseId._id === courseId
              );
              
              // Fetch lessons for each module
              courseModules = await Promise.all(courseModulesData.map(async (module: any) => {
                const lessonsResponse = await getLessonsByModuleId(module._id);
                let lessons: Lesson[] = [];
                
                if (lessonsResponse.status === 200 && lessonsResponse.data) {
                  lessons = lessonsResponse.data.map((lesson: any) => ({
                    _id: lesson._id,
                    title: lesson.title,
                    description: lesson.description,
                    lessonType: lesson.lessonType,
                    duration: lesson.duration || 0
                  }));
                }
                
                return {
                  _id: module._id,
                  title: module.title,
                  description: module.description,
                  lessons
                };
              }));
            }
            
            packageCourses.push({
              _id: courseId,
              title: courseDetails.title,
              description: courseDetails.description,
              thumbnail: courseDetails.thumbnail || "",
              createdAt: courseDetails.createdAt,
              language: courseDetails.language,
              desktopAvailable: courseDetails.desktopAvailable,
              PublishStatus: courseDetails.PublishStatus,
              courseViewPercent: courseProgressData.courseViewPercent,
              certificateSent: courseProgressData.certificateSent,
              status,
              modules: courseModules,
              moduleCount: courseModules.length,
              lessonCount: courseModules.reduce((total, module) => total + module.lessons.length, 0),
              expiry: courseExpiry,
              daysLeft: courseDaysLeft,
              // Removed purchaseId since we can't get it from the API
            });
            
          }
          
          processedPackages.push({
            packageId: packageId,
            packageName: packageName,
            packageThumbnail: packageThumbnail,
            createdAt: purchases[0]?.createdAt || new Date().toISOString(),
            courses: packageCourses
          });
        }
      }
      
      // -------------------- WEBINARS (channel access, no purchase required) --------------------
      // Webinars don't require a purchase - the backend already restricts the courses list to
      // webinars the user's channel can access, so any webinar returned here is playable.
      const includedCourseIds = new Set<string>();
      processedPackages.forEach(pkg => {
        pkg.courses.forEach(course => includedCourseIds.add(course._id));
      });

      const accessibleWebinars = coursesResponse.data.filter(
        (c: any) => c.courseType === 'Webinar' && c.PublishStatus === 'Published' && !includedCourseIds.has(c._id)
      );

      if (accessibleWebinars.length > 0) {
        const webinarCourses: PurchasedCourseWithPurchaseId[] = [];

        for (const courseDetails of accessibleWebinars) {
          let status: "yet_to_start" | "in_progress" | "completed" = "yet_to_start";
          let progressPercent = 0;
          let courseExpiry: string | undefined;
          let courseDaysLeft: number | undefined;

          try {
            const progressResponse = await getCourseProgress(courseDetails._id, userId);
            if (progressResponse.status === 200 && progressResponse.data) {
              const detailedProgress = progressResponse.data.getCourseProgress;
              progressPercent = detailedProgress.totalProgressPercent || progressPercent;
              courseExpiry = detailedProgress.expiry;
              courseDaysLeft = detailedProgress.daysLeft ?? undefined;

              if (detailedProgress.lessonProgress && detailedProgress.lessonProgress.length > 0) {
                const totalWatchTime = detailedProgress.lessonProgress.reduce((sum: number, lesson: any) =>
                  sum + (lesson.watchTime || 0), 0);

                if (totalWatchTime > 0 && progressPercent <= 0) {
                  progressPercent = 1;
                }
              }
            }
          } catch (error) {
            console.error(`Error fetching detailed progress for webinar ${courseDetails._id}:`, error);
          }

          if (progressPercent <= 0) {
            status = "yet_to_start";
          } else if (progressPercent >= 95) {
            status = "completed";
          } else {
            status = "in_progress";
          }

          // Fetch modules and lessons for this webinar
          const modulesResponse = await getAllCourseModules();
          let courseModules: Module[] = [];

          if (modulesResponse.status === 200 && modulesResponse.data) {
            const courseModulesData = modulesResponse.data.filter(
              (module: any) => module.courseId && module.courseId._id === courseDetails._id
            );

            courseModules = await Promise.all(courseModulesData.map(async (module: any) => {
              const lessonsResponse = await getLessonsByModuleId(module._id);
              let lessons: Lesson[] = [];

              if (lessonsResponse.status === 200 && lessonsResponse.data) {
                lessons = lessonsResponse.data.map((lesson: any) => ({
                  _id: lesson._id,
                  title: lesson.title,
                  description: lesson.description,
                  lessonType: lesson.lessonType,
                  duration: lesson.duration || 0
                }));
              }

              return {
                _id: module._id,
                title: module.title,
                description: module.description,
                lessons
              };
            }));
          }

          webinarCourses.push({
            _id: courseDetails._id,
            title: courseDetails.title,
            description: courseDetails.description,
            thumbnail: courseDetails.thumbnail || "",
            createdAt: courseDetails.createdAt,
            language: courseDetails.language,
            desktopAvailable: courseDetails.desktopAvailable,
            PublishStatus: courseDetails.PublishStatus,
            courseViewPercent: progressPercent,
            certificateSent: false,
            status,
            modules: courseModules,
            moduleCount: courseModules.length,
            lessonCount: courseModules.reduce((total, module) => total + module.lessons.length, 0),
            expiry: courseExpiry,
            daysLeft: courseDaysLeft,
          });
        }

        processedPackages.push({
          packageId: null,
          packageName: "My Webinars",
          packageThumbnail: "",
          createdAt: new Date().toISOString(),
          courses: webinarCourses
        });
      }

      setPurchasedPackages(processedPackages);

      // Initialize course progress state
      const initialProgress: Record<string, number> = {};
      processedPackages.forEach(pkg => {
        pkg.courses.forEach(course => {
          initialProgress[course._id] = course.courseViewPercent;
        });
      });
      setCourseProgress(initialProgress);
    } catch (error) {
      console.error("Error fetching purchased data:", error);
      setAlert({
        type: "error",
        message: "Please check your authentication and try again",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch the purchased data
  useEffect(() => {
    fetchPurchasedData();
    
    // Listen for purchase updates
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'purchaseUpdated') {
        fetchPurchasedData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Get icon based on lesson type
  // const getLessonIcon = (lessonType: string) => {
  //   switch (lessonType) {
  //     case 'video': return <Film size={16} className="text-purple-600" />;
  //     case 'document': return <FileText size={16} className="text-blue-600" />;
  //     case 'quiz': return <HelpCircle size={16} className="text-green-600" />;
  //     default: return <PlayCircle size={16} className="text-gray-600" />;
  //   }
  // };

  // Get status badge based on course status
  const getStatusBadge = (status: "yet_to_start" | "in_progress" | "completed") => {
    switch (status) {
      case "yet_to_start":
        return <span className="inline-flex items-center px-1 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-800">
          <Clock className="mr-0.5 h-2 w-2" />
          Yet to Start
        </span>;
      case "in_progress":
        return <span className="inline-flex items-center px-1 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">
          <PlayCircle className="mr-0.5 h-2 w-2" />
          In Progress
        </span>;
      case "completed":
        return <span className="inline-flex items-center px-1 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800">
          <CheckCircle className="mr-0.5 h-2 w-2" />
          Completed
        </span>;
      default:
        return <span className="inline-flex items-center px-1 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-800">
          Unknown
        </span>;
    }
  };

  const handleOpenVideoPage = (courseId: string, daysLeft?: number) => {
    // Prevent navigation to expired courses
    if (daysLeft !== undefined && daysLeft <= 0) {
      return;
    }
    navigate(`/video/${courseId}`);
  };

  return (
    <UserDashboardLayout>
      <div className="user-purchases">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-5">
        {alert && (
          <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
        )}
        
        {/* Header and Search Bar */}
        <div className="mb-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1.5">
            <div>
              <h1 className="text-md font-bold text-gray-900">My Learning Journey</h1>
              <p className="mt-0.5 text-sm text-gray-600">Track your progress and continue learning</p>
            </div>
            <div className="w-full md:w-64">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                  <Search className="h-3.5 w-3.5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search your courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-7 pr-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Filter Tabs - Improved styling for better clarity */}
        <div className="flex flex-wrap gap-1 mb-3">
          <button
            onClick={() => {
              setFilter("all");
              navigate("/UserPurchases?filter=all");
            }}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              filter === "all" 
                ? "bg-blue-500 text-white font-medium shadow-sm" 
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            All Courses
          </button>
          <button
            onClick={() => {
              setFilter("in_progress");
              navigate("/UserPurchases?filter=in_progress");
            }}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              filter === "in_progress" 
                ? "bg-blue-500 text-white font-medium shadow-sm" 
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            In Progress
          </button>
          <button
            onClick={() => {
              setFilter("completed");
              navigate("/UserPurchases?filter=completed");
            }}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              filter === "completed" 
                ? "bg-blue-500 text-white font-medium shadow-sm" 
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Completed
          </button>
          <button
            onClick={() => {
              setFilter("yet_to_start");
              navigate("/UserPurchases?filter=yet_to_start");
            }}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              filter === "yet_to_start" 
                ? "bg-blue-500 text-white font-medium shadow-sm" 
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Yet to Start
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-600">Loading your courses...</span>
          </div>
        ) : (
          <>
            {/* Purchased Packages/Courses List */}
            <div className="space-y-4">
              {purchasedPackages
                .filter(pkg => 
                  pkg.packageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  pkg.courses.some(course => 
                    course.title.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                )
                .map((pkg) => {
                  // Filter courses based on the selected filter
                  const filteredCourses = pkg.courses.filter(course => {
                    if (filter === "all") return true;
                    return course.status === filter;
                  });
                  
                  // If no courses match the filter, don't show this package
                  if (filteredCourses.length === 0) return null;
                  
                  // Unique key for groups without a real packageId (Individual Courses, My Webinars)
                  const groupKey = pkg.packageId || pkg.packageName;

                  return (
                    <div key={groupKey} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      {/* Package Header - Improved styling for better clarity */}
                      {pkg.packageId && (
                        <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8 rounded-md bg-blue-100 flex items-center justify-center">
                                <Package className="h-4 w-4 text-blue-600" />
                              </div>
                              <div className="ml-3">
                                <h2 className="text-sm font-semibold text-gray-900">{pkg.packageName}</h2>
                                <div className="flex items-center mt-1 text-xs text-gray-600">
                                  <Calendar className="mr-1 h-3 w-3" />
                                  <span>Purchased: {new Date(pkg.createdAt).toLocaleDateString()}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white text-gray-800 border border-gray-200 shadow-sm">
                                <BookOpen className="mr-1 h-3 w-3" />
                                {filteredCourses.length} {filteredCourses.length === 1 ? 'Course' : 'Courses'}
                              </span>
                              <button
                                onClick={() => togglePackage(groupKey)}
                                className="inline-flex items-center px-2.5 py-1 border border-gray-300 rounded-md shadow-xs text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                              >
                                {expandedPackages[groupKey] ? (
                                  <>
                                    <X className="mr-1 h-3 w-3" />
                                    Hide
                                  </>
                                ) : (
                                  <>
                                    <List className="mr-1 h-3 w-3" />
                                    Show
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Individual Courses Header - Improved styling for better clarity */}
                      {!pkg.packageId && (
                        <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div>
                              <h2 className="text-sm font-semibold text-gray-900">{pkg.packageName}</h2>
                              <div className="flex items-center mt-1 text-xs text-gray-600">
                                {pkg.packageName === "My Webinars" ? (
                                  <>
                                    <Eye className="mr-1 h-3 w-3" />
                                    <span>Channel Access</span>
                                  </>
                                ) : (
                                  <>
                                    <Calendar className="mr-1 h-3 w-3" />
                                    <span>Purchased: {new Date(pkg.createdAt).toLocaleDateString()}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white text-gray-800 border border-gray-200 shadow-sm">
                                <BookOpen className="mr-1 h-3 w-3" />
                                {filteredCourses.length} {filteredCourses.length === 1 ? 'Course' : 'Courses'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Package Content - Courses */}
                      {(!pkg.packageId || expandedPackages[groupKey]) && (
                        <div className="p-3">
                          {filteredCourses.length === 0 ? (
                            <div className="text-center py-8">
                              <BookOpen className="mx-auto h-8 w-8 text-gray-400" />
                              <h3 className="mt-2 text-sm font-medium text-gray-900">No courses found</h3>
                              <p className="mt-1 text-xs text-gray-500">No courses match the current filter.</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {filteredCourses.map((course) => (
                                <div 
                                  key={course._id} 
                                  className={`bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-150 ${course.daysLeft !== undefined && course.daysLeft <= 0 ? 'opacity-50 pointer-events-none' : ''}`}
                                >
                                  {/* Course Thumbnail - 16:9 Aspect Ratio */}
                                  <div className="relative aspect-video w-full">
                                    {course.thumbnail ? (
                                      (() => {
                                        const thumbnailUrl = getCourseThumbnail(course.thumbnail);
                                        const isVideo = isVideoUrl(thumbnailUrl);
                                        
                                        if (isVideo) {
                                          return (
                                            <OptimizedVideoThumbnail 
                                              src={thumbnailUrl} 
                                              alt={course.title} 
                                            />
                                          );
                                        } else {
                                          return (
                                            <OptimizedImageThumbnail 
                                              src={thumbnailUrl} 
                                              alt={course.title} 
                                            />
                                          );
                                        }
                                      })()
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
                                        <BookOpen className="h-6 w-6 text-blue-500" />
                                      </div>
                                    )}
                                    <div className="absolute top-2 right-2">
                                      {getStatusBadge(course.status)}
                                    </div>
                                    {/* Expired overlay */}
                                    {course.daysLeft !== undefined && course.daysLeft <= 0 && (
                                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-t-lg">
                                        <span className="text-white font-bold text-xs bg-red-600 px-2 py-1 rounded">
                                          EXPIRED
                                        </span>
                                      </div>
                                    )}
                                    <button
                                      onClick={() => handleOpenVideoPage(course._id, course.daysLeft)}
                                      className={`absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity duration-150 ${course.daysLeft !== undefined && course.daysLeft <= 0 ? 'hidden' : ''}`}
                                      disabled={course.daysLeft !== undefined && course.daysLeft <= 0}
                                    >
                                      <div className="bg-white rounded-full p-2 shadow-lg">
                                        <PlayCircle className="h-4 w-4 text-blue-600" />
                                      </div>
                                    </button>
                                  </div>
                                  
                                  {/* Course Content - Improved styling for better clarity */}
                                  <div className="p-3">
                                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{course.title}</h3>
                                    <div className="mt-1 text-xs text-gray-600 line-clamp-2">
                                        <ReactMarkdown>
                                          {course.description || ""}
                                        </ReactMarkdown>
                                      </div>
                                    
                                    <div className="mt-2 flex flex-wrap gap-1">
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        <BookOpen className="mr-1 h-3 w-3" />
                                        {course.moduleCount} {course.moduleCount === 1 ? 'Module' : 'Modules'}
                                      </span>
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                        <FileText className="mr-1 h-3 w-3" />
                                        {course.lessonCount} {course.lessonCount === 1 ? 'Lesson' : 'Lessons'}
                                      </span>
                                      {/* Display days left if course is in progress and has 10 or fewer days left */}
                                      {course.status === 'in_progress' && course.daysLeft !== undefined && course.daysLeft <= 10 && course.daysLeft >= 0 && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                          <Clock className="mr-1 h-3 w-3" />
                                          {course.daysLeft} {course.daysLeft === 1 ? 'day' : 'days'} left
                                        </span>
                                      )}
                                    </div>
                                    
                                    {/* Progress */}
                                    
                                    {/* Action Button - Improved styling */}
                                    <button
                                      onClick={() => handleOpenVideoPage(course._id, course.daysLeft)}
                                      className={`w-full mt-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-md hover:from-blue-600 hover:to-indigo-700 transition-all duration-150 flex items-center justify-center ${course.daysLeft !== undefined && course.daysLeft <= 0 ? 'opacity-50 cursor-not-allowed' : ''} shadow-sm`}
                                      disabled={course.daysLeft !== undefined && course.daysLeft <= 0}
                                    >
                                      <Eye className="mr-1 h-3 w-3" />
                                      Continue Learning
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
                .filter(Boolean) // Remove null packages
              }

              {/* Empty state - Improved styling for better clarity */}
              {purchasedPackages.length === 0 && !loading && (
                <div className="text-center py-12">
                  <div className="mx-auto h-12 w-12 text-gray-400">
                    <Package className="h-12 w-12 mx-auto text-gray-300" />
                  </div>
                  <h3 className="mt-3 text-base font-medium text-gray-900">No purchases yet</h3>
                  <p className="mt-1 text-md text-gray-500">
                    You haven't purchased any courses or packages yet.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={() => navigate('/user-offerings')}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                    >
                      Browse Offerings
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      </div>
    </UserDashboardLayout>
  );
};

export default UserPurchase;